import chroma from 'chroma-js'
import { sample } from 'lodash-es'

export const cbvThemeNames = [
  'lighthouse',
  'custom',
  'greenToRed',
  'redToGreen',
  'grayscale',
  'random'
] as const

const colorBrewerQualitativeColorNames = [
  'Accent',
  'Dark2',
  'Paired',
  'Pastel1',
  'Pastel2',
  'Set1',
  'Set2',
  'Set3'
] as const

export type CBVThemes = (typeof cbvThemeNames)[number]

// From light to dark
const themeToRGBMap: Record<CBVThemes, string[]> = {
  lighthouse: ['#EBF1FF', '#82ACFF', '#3C5685'],
  redToGreen: ['#D01F31', '#F68121', '#FBDD0B', '#14B025'],
  greenToRed: ['#14B025', '#FBDD0B', '#F68121', '#D01F31'],
  grayscale: ['#F2F3F4', '#808285'],
  /**
   * On app launch, bind to a random qualitative color scale
   */
  random: chroma.brewer[sample(colorBrewerQualitativeColorNames)!],
  /**
   * Placeholder/fallback, there should be logic created to
   * trigger alternative behaviour when this key is detected
   * in program flow.
   */
  custom: ['#FFF', '#000']
}

/**
 * Function to retrieve theme colors
 *
 * @param themeName one of `cbvThemeNames`
 * @returns array of 6 character CSS color hex codes corresponding to a `CBVTheme`
 */
export const getHexCodesFromCBVTheme = (themeName: CBVThemes) => {
  let themeColorCodes = themeToRGBMap[themeName]

  if (themeColorCodes == null) {
    // fallback in the chance that theme got removed
    themeColorCodes = themeToRGBMap.custom
  }

  return themeColorCodes.map((code) => chroma(code).hex())
}

/**
 * Helper to interpolate additional color values by sampling gradient generated by input
 *
 * @param hexCodes CSS color hex codes used to produce that gradient to sample from
 * @param range number of CSS color hex codes to return, sampled evenly within the gradient
 * @returns CSS color hex codes
 */
export const getGradientSamplesFromHexCodes = (
  hexCodes: string[],
  range: number
) => {
  let colorSet

  if (hexCodes.length > range) {
    colorSet =
      range > 1
        ? hexCodes.slice(0, range - 1).concat(hexCodes[hexCodes.length - 1])
        : hexCodes.slice(0, range)
  } else if (hexCodes.length < range) {
    colorSet = chroma.scale(hexCodes).colors(range)
  } else {
    colorSet = hexCodes
  }

  return colorSet
}
